import { z } from 'zod';
import { RACE_SIZES } from '../../gen/modes';
import { MODE_WANTS, ROOM_MODES, RoomCodeSchema, type RoomMode } from './live';
import { FriendRequestRowSchema, PlayerIdSchema, PlayerNameSchema } from './schema';

/**
 * The player's hub (docs/FRIENDS.md, step 2): one socket per open app to its own Durable Object,
 * which marks the player online for friends and passes invites down as they come. JSON, checked
 * with these schemas on both sides.
 */

const int = () => z.number().int();

/** A friend counts as online while their hub last marked them within this long. */
export const ONLINE_FRESH_MS = 90_000;
/** The hub refreshes its player's mark at most this often (on the socket's pings). */
export const ONLINE_MARK_MS = 60_000;
/** An invite lasts as long as a waiting room may (it closes after 30 idle minutes). */
export const INVITE_MS = 30 * 60_000;
/** The most invites an inbox keeps (the oldest go first). */
export const MAX_INVITES = 20;

/** Whether a player marked online at `onlineAt` (0: never or not now) counts as online at `now`. */
export function isOnline(onlineAt: number, now: number): boolean {
  return onlineAt > 0 && now - onlineAt < ONLINE_FRESH_MS;
}

/** A friend's invite to their room. */
export const InviteSchema = z.object({
  code: RoomCodeSchema,
  mode: z.enum(ROOM_MODES),
  size: z.enum(RACE_SIZES),
  from: z.object({ player: PlayerIdSchema, name: PlayerNameSchema }),
  at: int(),
  expires: int(),
});
export type Invite = z.infer<typeof InviteSchema>;

/** POST /rooms/:code/invite: the host invites a friend. */
export const InviteRequestSchema = z.strictObject({ friend: PlayerIdSchema });

/**
 * A list of invites, each read on its own: one this game can't read (a mode added since) is left
 * out rather than spoiling the rest.
 */
const InviteListSchema = z.array(z.unknown()).transform((list) =>
  list.flatMap((item) => {
    const invite = InviteSchema.safeParse(item);
    return invite.success ? [invite.data] : [];
  }),
);

/** GET /inbox: what waits for the player: invites, and friend requests (from 2026-10-28). */
export const InboxSchema = z.object({
  invites: InviteListSchema,
  requests: z.array(FriendRequestRowSchema).default([]),
});
export type Inbox = z.infer<typeof InboxSchema>;

/**
 * Push notifications (docs/FRIENDS.md, step 4): what pushes (a game invite, a friend request, a
 * friend waiting in the player's room, and someone taking first place on today's Daily from the
 * player), never anything else.
 */
export type PushKind = 'invite' | 'request' | 'room' | 'daily';

/** At most one push per sender and kind this often. */
export const PUSH_AGAIN_MS = 10 * 60_000;

/** A push's words, after the sender's name (`who`). */
export function pushText(kind: PushKind, who: string, mode?: RoomMode): string {
  if (kind === 'invite') return `${who} ${MODE_WANTS[mode ?? 'race']}`;
  if (kind === 'request') return `${who} wants to be friends.`;
  if (kind === 'daily') return `${who} beat your time on today's Daily. Take first place back!`;
  return `${who} is in your room. Come back to start!`;
}

/** PUT /me/push: this phone's push token (APNs: hex). Android (Firebase) comes later. */
export const PushTokenSchema = z.strictObject({
  platform: z.literal('ios'),
  token: z.string().regex(/^[0-9a-fA-F]{64,200}$/),
});
export type PushToken = z.infer<typeof PushTokenSchema>;

/** POST /me/push/test: whether a test went to this player's phone, and Apple's answer. */
export const PushTestSchema = z.object({
  sent: z.boolean(),
  status: int(),
  reason: z.string().optional(),
});
export type PushTest = z.infer<typeof PushTestSchema>;

/** POST /me/hub/ticket: a one-use ticket for the hub's socket. */
export const HubTicketSchema = z.object({
  ticket: z.string().min(16).max(64),
  player: PlayerIdSchema,
});

export const HubClientMessageSchema = z.discriminatedUnion('type', [
  /** Whether friends may see this player online (Settings > ONLINE). */
  z.strictObject({ type: z.literal('hello'), visible: z.boolean() }),
  z.strictObject({ type: z.literal('ping'), at: int() }),
]);
export type HubClientMessage = z.infer<typeof HubClientMessageSchema>;

export const HubServerMessageSchema = z.discriminatedUnion('type', [
  /** Everything waiting, on connecting and whenever it changes. */
  z.object({ type: z.literal('inbox'), invites: InviteListSchema }),
  /** A new invite, just now. */
  z.object({ type: z.literal('invite'), invite: InviteSchema }),
  z.object({ type: z.literal('pong'), at: int() }),
  /** Something else in the inbox changed (a friend request): ask GET /inbox again. */
  z.object({ type: z.literal('changed') }),
]);
export type HubServerMessage = z.infer<typeof HubServerMessageSchema>;

/** The invites still open at `now`, newest first, at most {@link MAX_INVITES}. */
export function openInvites(invites: readonly Invite[], now: number): Invite[] {
  return invites
    .filter((i) => i.expires > now)
    .sort((a, b) => b.at - a.at)
    .slice(0, MAX_INVITES);
}
